import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogIn, LogOut, CalendarDays, Timer, Wallet, Megaphone, ArrowRight, Sparkles, Palmtree, IndianRupee,
} from "lucide-react";
import { papi, errText } from "../portalApi";
import { usePortal } from "../PortalApp";
import { Card, StatusBadge, Spinner, fmtDate, fmtTime, fmtMoney } from "../ui";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export default function Dashboard() {
  const { emp } = usePortal();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await papi.get("/dashboard");
      setD(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not load your dashboard."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkedIn = Boolean((d?.todayRecord?.checkIn && !d?.todayRecord?.checkOut) || (d?.todayRecord?.checkIn2 && !d?.todayRecord?.checkOut2));
  useEffect(() => {
    if (!checkedIn) return undefined;
    const t = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(t);
  }, [checkedIn]);

  if (!d) return <Spinner />;

  const punch = async (kind) => {
    setBusy(true);
    try {
      if (kind === "in") {
        await papi.post("/attendance/checkin", {});
        toast.success("Checked in. Have a great day!");
      } else {
        const r = await papi.post("/attendance/checkout");
        toast.success(`Checked out — ${r.data.totalHours}h logged today.`);
      }
      await load();
    } catch (err) {
      toast.error(errText(err, "Action failed."));
    } finally {
      setBusy(false);
    }
  };

  const rec = d.todayRecord;
  const canPunch = !["Holiday", "Leave", "Weekly Off"].includes(d.todayStatus);
  const hasSplit = Boolean(d.settings?.shift2Start);
  const activeIn = rec?.checkIn2 && !rec?.checkOut2 ? rec.checkIn2 : rec?.checkIn && !rec?.checkOut ? rec.checkIn : null;
  const elapsed = activeIn
    ? ((Date.now() - new Date(activeIn).getTime()) / 3600000).toFixed(1)
    : null;
  const totalLeaveAvailable = d.leaveBalances.reduce((s, b) => s + b.available, 0);
  const weekPct = Math.min(100, Math.round((100 * d.weekHours) / (d.weekExpected || 1)));

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <p className="eyebrow">{fmtDate(new Date().toISOString())}</p>
        <h1 className="mt-2 font-serif text-3xl text-navy-900 sm:text-4xl" data-testid="dashboard-greeting">
          {greeting()}, {d.firstName}.
        </h1>
        <p className="mt-1.5 text-sm text-slate-500">Here is your day at a glance.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card testid="clock-card" className="border-navy-950 bg-navy-950 text-slate-300 lg:col-span-1">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">Today</p>
            <StatusBadge status={d.todayStatus} />
          </div>
          {canPunch ? (
            <div className="mt-6">
              {rec?.checkIn ? (
                <div className="space-y-2 text-sm text-slate-400">
                  <p data-testid="shift1-summary">
                    {hasSplit && <span className="font-semibold text-gold">Shift 1 · </span>}
                    In <span className="font-semibold text-ivory" data-testid="checkin-time">{fmtTime(rec.checkIn)}</span>
                    {rec.checkOut ? (
                      <> · Out <span className="font-semibold text-ivory">{fmtTime(rec.checkOut)}</span></>
                    ) : (
                      <> · <span className="font-serif text-2xl text-gold" data-testid="elapsed-timer">{elapsed}h</span></>
                    )}
                  </p>
                  {rec.checkIn2 && (
                    <p data-testid="shift2-summary">
                      <span className="font-semibold text-gold">Shift 2 · </span>
                      In <span className="font-semibold text-ivory" data-testid="checkin2-time">{fmtTime(rec.checkIn2)}</span>
                      {rec.checkOut2 ? (
                        <> · Out <span className="font-semibold text-ivory">{fmtTime(rec.checkOut2)}</span></>
                      ) : (
                        <> · <span className="font-serif text-2xl text-gold" data-testid="elapsed-timer-2">{elapsed}h</span></>
                      )}
                    </p>
                  )}
                  {rec.checkOut && (!hasSplit || rec.checkOut2) && (
                    <p data-testid="checkout-summary">Total <span className="font-semibold text-gold">{rec.totalHours}h</span> logged today</p>
                  )}
                </div>
              ) : (
                <p className="text-sm leading-relaxed text-slate-400">
                  You have not checked in yet. Your shift: {d.settings.shiftLabel || `${d.settings.shiftStart} – ${d.settings.shiftEnd}`}.
                </p>
              )}
              <div className="mt-6">
                {!rec?.checkIn && (
                  <button
                    onClick={() => punch("in")}
                    disabled={busy}
                    className="btn-gold w-full justify-center disabled:opacity-60"
                    data-testid="checkin-btn"
                  >
                    <LogIn className="h-4 w-4" aria-hidden /> Check In
                  </button>
                )}
                {rec?.checkIn && !rec?.checkOut && (
                  <button
                    onClick={() => punch("out")}
                    disabled={busy}
                    className="btn-ivory w-full justify-center disabled:opacity-60"
                    data-testid="checkout-btn"
                  >
                    <LogOut className="h-4 w-4" aria-hidden /> Check Out
                  </button>
                )}
                {hasSplit && rec?.checkOut && !rec?.checkIn2 && (
                  <button
                    onClick={() => punch("in")}
                    disabled={busy}
                    className="btn-gold w-full justify-center disabled:opacity-60"
                    data-testid="checkin2-btn"
                  >
                    <LogIn className="h-4 w-4" aria-hidden /> Check In — Shift 2
                  </button>
                )}
                {rec?.checkIn2 && !rec?.checkOut2 && (
                  <button
                    onClick={() => punch("out")}
                    disabled={busy}
                    className="btn-ivory w-full justify-center disabled:opacity-60"
                    data-testid="checkout2-btn"
                  >
                    <LogOut className="h-4 w-4" aria-hidden /> Check Out — Shift 2
                  </button>
                )}
                {rec?.checkOut && (!hasSplit || rec?.checkOut2) && (
                  <p className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-center text-xs text-slate-400" data-testid="day-complete-note">
                    Attendance completed for today. See you tomorrow!
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="mt-6 rounded-lg border border-white/10 bg-white/5 px-4 py-4 text-sm leading-relaxed text-slate-300" data-testid="today-special-note">
              {d.todayStatus === "Holiday" && "Today is a company holiday. Enjoy your day off!"}
              {d.todayStatus === "Leave" && "You are on approved leave today."}
              {d.todayStatus === "Weekly Off" && "Today is the weekly off. Rest and recharge!"}
            </p>
          )}
        </Card>

        <Card testid="week-hours-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><Timer className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">This Week</p>
          </div>
          <p className="mt-4 font-serif text-4xl text-navy-900" data-testid="week-hours">
            {d.weekHours}<span className="text-lg text-slate-400"> / {d.weekExpected}h</span>
          </p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-gold transition-all duration-500" style={{ width: `${weekPct}%` }} />
          </div>
          <Link to="/portal/timesheets" className="link-underline mt-4 text-xs" data-testid="goto-timesheets">
            Log timesheet <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        </Card>

        <Card testid="leave-balance-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><CalendarDays className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">Leave Balance</p>
          </div>
          <p className="mt-4 font-serif text-4xl text-navy-900" data-testid="leave-available-total">
            {totalLeaveAvailable}<span className="text-lg text-slate-400"> days</span>
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {d.leaveBalances.filter((b) => b.allocated > 0).map((b) => (
              <span key={b.code} className="rounded-full border border-navy-900/10 bg-ivory px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                {b.code} {b.available}/{b.allocated}
              </span>
            ))}
          </div>
          <Link to="/portal/leave" className="link-underline mt-4 text-xs" data-testid="goto-leave">
            Apply for leave <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        </Card>

        <Card testid="compensation-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><IndianRupee className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">
              {d.compensation?.type === "Stipend" ? "Monthly Stipend" : "Monthly Compensation"}
            </p>
          </div>
          {d.compensation?.amount ? (
            <div className="mt-4">
              <p className="font-serif text-4xl text-navy-900" data-testid="compensation-amount">{fmtMoney(d.compensation.amount)}</p>
              <p className="mt-1 text-xs text-slate-500">
                {d.compensation.frequency}
                {d.compensation.effectiveDate ? ` · effective ${fmtDate(d.compensation.effectiveDate)}` : ""}
              </p>
              {d.compensation.breakdown?.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="compensation-breakdown">
                  {d.compensation.breakdown.map((b) => (
                    <div key={b.label} className="flex items-center justify-between rounded-lg bg-ivory px-3 py-1.5 text-xs">
                      <span className="text-slate-500">{b.label}</span>
                      <span className="font-semibold text-navy-900">{fmtMoney(b.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {d.compensation.notes && <p className="mt-3 text-xs italic leading-relaxed text-slate-400">{d.compensation.notes}</p>}
            </div>
          ) : (
            <p className="mt-4 text-sm leading-relaxed text-slate-500" data-testid="compensation-notes">
              {d.compensation?.notes || "Not set yet — HR will update your compensation details here."}
            </p>
          )}
        </Card>

        <Card testid="month-summary-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><Sparkles className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">This Month</p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-center">
            {[
              ["Present", d.monthSummary.present],
              ["Leave", d.monthSummary.leaveDays],
              ["Holidays", d.monthSummary.holidays],
              ["Working Days", d.monthSummary.workingDays],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl bg-ivory px-3 py-3">
                <p className="font-serif text-2xl text-navy-900">{val}</p>
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
              </div>
            ))}
          </div>
          <Link to="/portal/attendance" className="link-underline mt-4 text-xs" data-testid="goto-attendance">
            View attendance <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        </Card>

        <Card testid="payslip-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><Wallet className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">Latest Payslip</p>
          </div>
          {d.latestPayslip ? (
            <div className="mt-4">
              <p className="font-serif text-2xl text-navy-900" data-testid="latest-payslip-net">{fmtMoney(d.latestPayslip.net)}</p>
              <p className="mt-1 text-xs text-slate-500">
                {d.latestPayslip.compensationType} · {d.latestPayslip.month}
              </p>
              <div className="mt-2"><StatusBadge status={d.latestPayslip.status} /></div>
              <Link to="/portal/payslips" className="link-underline mt-4 text-xs" data-testid="goto-payslips">
                View payslips <ArrowRight className="h-3 w-3" aria-hidden />
              </Link>
            </div>
          ) : (
            <p className="mt-4 text-sm leading-relaxed text-slate-500" data-testid="no-payslip-note">
              No payslips yet. Once payroll processes your first salary or stipend, it will appear here.
            </p>
          )}
        </Card>

        <Card testid="holidays-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><Palmtree className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">Upcoming Holidays</p>
          </div>
          <div className="mt-4 space-y-3" data-testid="upcoming-holidays">
            {d.upcomingHolidays.length === 0 && <p className="text-sm text-slate-500">No upcoming holidays this year.</p>}
            {d.upcomingHolidays.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-3 rounded-xl bg-ivory px-4 py-2.5">
                <div>
                  <p className="text-sm font-semibold text-navy-900">{h.name}</p>
                  <p className="text-[11px] text-slate-500">{h.type}</p>
                </div>
                <p className="text-xs font-semibold text-gold-dark">{fmtDate(h.date)}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card testid="announcements-card" className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><Megaphone className="h-5 w-5" aria-hidden /></span>
            <p className="text-sm font-semibold text-navy-900">Announcements</p>
          </div>
          <div className="mt-4 space-y-3" data-testid="announcements-list">
            {d.announcements.length === 0 && <p className="text-sm text-slate-500">No announcements right now.</p>}
            {d.announcements.map((a) => (
              <div key={a.id} className="rounded-xl border border-navy-900/5 bg-ivory px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-navy-900">{a.title}</p>
                  <span className="rounded-full bg-gold/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-dark">{a.category}</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{a.body}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card testid="profile-completion-card">
          <p className="text-sm font-semibold text-navy-900">Profile Completion</p>
          <p className="mt-3 font-serif text-4xl text-navy-900" data-testid="profile-completion">{d.profileCompletion}%</p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-gold transition-all duration-500" style={{ width: `${d.profileCompletion}%` }} />
          </div>
          {d.profileCompletion < 100 && (
            <Link to="/portal/profile" className="link-underline mt-4 text-xs" data-testid="goto-profile">
              Complete your profile <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          )}
        </Card>
      </div>
    </div>
  );
}
